/**
 * MessageBubble — one chat message with edit + reaction UI. Extracted from
 * MessagesPage so each message owns its own edit/picker state (the parent
 * could've held a single `editingId` but per-bubble state is cleaner once
 * the bubble has multiple ephemeral concerns: edit draft, reaction picker
 * visibility, busy flag, error).
 */
import React, { useState } from "react";

import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo, fmtUtc } from "../../lib/format";
import {
  ALLOWED_REACTIONS,
  type ChatMessage,
  editChatMessage,
  toggleChatReaction,
} from "../../services/chatApi";

/**
 * One message bubble with edit + reaction UI. Lives next to the parent so
 * each message has its own edit/picker state (a single `editingId` on the
 * parent worked too but this is cleaner once the bubble has more state).
 */
export const MessageBubble: React.FC<{
  message: ChatMessage;
  mine: boolean;
  onMutated: () => void;
}> = ({ message, mine, onMutated }) => {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.body);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = (): void => {
    setDraft(message.body);
    setEditing(true);
    setError(null);
  };
  const cancelEdit = (): void => {
    setEditing(false);
    setError(null);
  };
  const saveEdit = async (): Promise<void> => {
    if (!token) return;
    const body = draft.trim();
    if (!body) {
      setError("can't save an empty message");
      return;
    }
    setBusy(true);
    try {
      await editChatMessage(message.message_id, body, token);
      setEditing(false);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleReact = async (emoji: string): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setPickerOpen(false);
    try {
      await toggleChatReaction(message.message_id, emoji, token);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const bubbleCls =
    "max-w-[80%] rounded-lg px-3 py-2 text-sm relative group " +
    (mine ? "bg-primary-600 text-white" : "bg-secondary-100 text-secondary-900");

  return (
    <div className={"flex flex-col " + (mine ? "items-end" : "items-start")}>
      <div className={bubbleCls}>
        {!mine && (
          <div className="text-[10px] font-semibold opacity-70 mb-0.5">
            {message.author_name || message.author_email}
          </div>
        )}
        {editing ? (
          <div className="space-y-1">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="w-full text-sm border border-secondary-300 rounded px-2 py-1 text-secondary-900 bg-white"
            />
            <div className="flex justify-end gap-2 text-xs">
              <button
                type="button"
                onClick={cancelEdit}
                disabled={busy}
                className={mine ? "text-primary-100 hover:text-white" : "text-secondary-500 hover:text-secondary-900"}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveEdit()}
                disabled={busy || !draft.trim()}
                className="font-semibold underline underline-offset-2"
              >
                {busy ? "saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div
            className={
              "whitespace-pre-wrap break-words " +
              (message.deleted ? "italic opacity-60" : "")
            }
          >
            {message.body}
          </div>
        )}
        <div
          className={
            "text-[10px] mt-0.5 flex items-center gap-1.5 " +
            (mine ? "text-primary-100" : "text-secondary-500")
          }
          title={message.posted_utc ? fmtUtc(message.posted_utc) : undefined}
        >
          <span>{message.posted_utc ? fmtAgo(message.posted_utc) : ""}</span>
          {message.edited_utc && (
            <span
              className="italic"
              title={`Edited ${fmtUtc(message.edited_utc)}`}
            >
              (edited)
            </span>
          )}
          {mine && !editing && !message.deleted && (
            <button
              type="button"
              onClick={startEdit}
              className="opacity-0 group-hover:opacity-100 transition underline underline-offset-2 hover:text-white"
              aria-label="Edit message"
            >
              Edit
            </button>
          )}
          {!editing && !message.deleted && (
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className={
                "opacity-0 group-hover:opacity-100 transition px-1 leading-none " +
                (mine ? "hover:text-white" : "hover:text-secondary-900")
              }
              aria-label="Add reaction"
              title="Add reaction"
            >
              +☺
            </button>
          )}
        </div>
        {pickerOpen && (
          <div
            className="absolute -top-9 left-0 z-10 flex gap-0.5 bg-white border border-secondary-200 rounded-lg shadow-md p-1"
            onMouseLeave={() => setPickerOpen(false)}
          >
            {ALLOWED_REACTIONS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => void toggleReact(e)}
                disabled={busy}
                className="text-base px-1.5 py-0.5 rounded hover:bg-secondary-100"
                aria-label={`react with ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Reaction chips render below the bubble so they don't crowd the body. */}
      {message.reactions.length > 0 && (
        <div className={"mt-1 flex flex-wrap gap-1 " + (mine ? "justify-end" : "")}>
          {message.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              onClick={() => void toggleReact(r.emoji)}
              disabled={busy}
              className={
                "text-xs px-1.5 py-0.5 rounded-full border flex items-center gap-1 " +
                (r.i_reacted
                  ? "bg-primary-50 border-primary-300 text-primary-800"
                  : "bg-white border-secondary-200 text-secondary-700 hover:bg-secondary-50")
              }
              title={
                r.i_reacted
                  ? `Remove your ${r.emoji} (${r.count})`
                  : `React with ${r.emoji} (${r.count})`
              }
            >
              <span>{r.emoji}</span>
              <span className="font-semibold">{r.count}</span>
            </button>
          ))}
        </div>
      )}
      {error && (
        <div className="text-[11px] text-error-700 mt-0.5">{error}</div>
      )}
    </div>
  );
};

export default MessageBubble;
